// crewbit-admin/src/hooks/useProposeCategorization.ts
//
// Tier 2 Phase B §WorldClass.3 — tax automation substrate.
// Admin paired hook for propose_expense_categorization RPC (Migration 273).
// Mirrors crewbit-mobile/hooks/useProposeCategorization.ts.
//
// Read-only RPC (creates a proposal but does NOT create an expense yet); no
// MONEY_MUTATION_EVENT emit at this hook. Confirm step lineage emits.

import { useCallback } from 'react';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import type { ProposedCategorization } from '../types/proposals';

export interface ProposeCategorizationArgs {
  businessId: string;
  vendorName: string | null;
  amount: number;
  description?: string | null;
  paymentMethod?: string | null;
  ocrData?: Record<string, unknown> | null;
  // §WorldClass.3 Phase B (Migration 283) — 9 operator-domain inputs that
  // thread through confirm_expense_proposal → record_expense.
  date?: string | null;
  isHstExempt?: boolean | null;
  notes?: string | null;
  projectId?: string | null;
  jobId?: string | null;
  phaseId?: string | null;
  mileageKm?: number | null;
  receiptUrl?: string | null;
  receiptFilename?: string | null;
  // Wave 7 — vendor entity FK (Step 0 migration extended the RPC sig).
  vendorId?: string | null;
  // Wave 8 Step 3 — per-expense tax_type (QC picker pre-fill). Step 0 migration
  // grew the RPC sig to 17 args.
  taxType?: string | null;
}

export type ProposeCategorizationHookResult = MoneyRpcResult<{
  proposal_id: string;
  proposed: ProposedCategorization;
  expires_at: string;
}>;

export function useProposeCategorization() {
  return useCallback(
    async (args: ProposeCategorizationArgs): Promise<ProposeCategorizationHookResult> => {
      return callMoneyRpc<{
        proposal_id: string;
        proposed: ProposedCategorization;
        expires_at: string;
      }>('propose_expense_categorization', {
        p_business_id: args.businessId,
        p_vendor_name: args.vendorName,
        p_amount: args.amount,
        p_description: args.description ?? null,
        p_payment_method: args.paymentMethod ?? null,
        p_ocr_data: args.ocrData ?? null,
        p_date: args.date ?? null,
        p_is_hst_exempt: args.isHstExempt ?? null,
        p_notes: args.notes ?? null,
        p_project_id: args.projectId ?? null,
        p_job_id: args.jobId ?? null,
        p_phase_id: args.phaseId ?? null,
        p_mileage_km: args.mileageKm ?? null,
        p_receipt_url: args.receiptUrl ?? null,
        p_receipt_filename: args.receiptFilename ?? null,
        p_vendor_id: args.vendorId ?? null,
        p_tax_type: args.taxType ?? null,
      });
    },
    [],
  );
}
